import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "./primitives";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613738053817-7f0983aa456d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHx0YXJvdCUyMGNhcmRzJTIwZGFyayUyMGdvbGR8ZW58MHx8fHwxNzg2MDk4OTM1fDA&ixlib=rb-4.1.0&w=1920&q=72&auto=format";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Tarot cards in candlelight"
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 z-10 bg-[#07040D]"
      />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#07040D] via-[#07040D]/30 to-[#07040D]/60" />

      <div className="relative z-20 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-end pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-8"
        >
          <span className="text-xs tracking-[0.32em] uppercase text-[#D4AF37]">
            Tarot · Energy Cleansing · Healing
          </span>
        </motion.div>

        <h1 className="font-serif-display font-light text-[#EAE0D5] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          <MaskLine delay={0.4}>More than just</MaskLine>
          <MaskLine delay={0.55}>another</MaskLine>
          <MaskLine delay={0.7} className="italic text-[#D4AF37]">
            Tarot Reading.
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-[#A89FA8] text-base sm:text-lg leading-relaxed"
        >
          An intuitive space for self-discovery, healing and transformation.
        </motion.p>

        <motion.a
          href="#services"
          data-testid="hero-scroll-cue"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 inline-flex items-center gap-3 text-[#EAE0D5] group w-fit"
        >
          <span className="w-11 h-11 rounded-full border border-[#D4AF37]/40 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-300">
            <ArrowDown
              className="w-4 h-4 text-[#D4AF37] animate-bounce"
              strokeWidth={1.5}
            />
          </span>
          <span className="text-xs tracking-[0.2em] uppercase text-[#A89FA8] group-hover:text-[#EAE0D5] transition-colors">
            Explore the offerings
          </span>
        </motion.a>
      </div>
    </section>
  );
};
